import type {HttpClient} from '@sollapay/http-client';
import type {
  AddTrustAccountMemberPayload,
  ApiResponse,
  ChangeTrustAccountMemberRolePayload,
  TrustAccountMemberCandidateDTO,
  TrustAccountMemberChangeDTO,
  TrustAccountMemberEventDTO,
  TrustAccountMemberRemovedDTO,
  TrustAccountMemberRoleChangeDTO,
  TrustAccountMembersDTO,
  TrustAccountSuggestedTeamDTO
} from '@sollapay/types';

const membersPath = (trustAccountId: string) => `/trust_accounts/${trustAccountId}/members`;

/**
 * API client functions for trust account members
 */
export const trustAccountMembersApi = {
  /**
   * The team of the caller's latest trust account, offered when creating the next one
   */
  fetchSuggestedTeam: async (
    client: HttpClient,
    excludeTrustAccountId?: string
  ): Promise<ApiResponse<TrustAccountSuggestedTeamDTO | null>> => {
    return client.get<ApiResponse<TrustAccountSuggestedTeamDTO | null>>(
      '/trust_accounts/members/suggested_team',
      {params: {excludeTrustAccountId}}
    );
  },

  /**
   * The account's trustee and active members
   */
  fetchMembers: async (
    client: HttpClient,
    trustAccountId: string
  ): Promise<ApiResponse<TrustAccountMembersDTO>> => {
    return client.get<ApiResponse<TrustAccountMembersDTO>>(membersPath(trustAccountId));
  },

  /**
   * Firm users the trustee can still add to the account
   */
  fetchCandidates: async (
    client: HttpClient,
    trustAccountId: string
  ): Promise<ApiResponse<TrustAccountMemberCandidateDTO[]>> => {
    return client.get<ApiResponse<TrustAccountMemberCandidateDTO[]>>(
      `${membersPath(trustAccountId)}/candidates`
    );
  },

  /**
   * The account's most recent membership changes, newest first
   */
  fetchEvents: async (
    client: HttpClient,
    trustAccountId: string
  ): Promise<ApiResponse<TrustAccountMemberEventDTO[]>> => {
    return client.get<ApiResponse<TrustAccountMemberEventDTO[]>>(
      `${membersPath(trustAccountId)}/events`
    );
  },

  addMember: async (
    client: HttpClient,
    trustAccountId: string,
    payload: AddTrustAccountMemberPayload
  ): Promise<ApiResponse<TrustAccountMemberChangeDTO>> => {
    return client.post<ApiResponse<TrustAccountMemberChangeDTO>, AddTrustAccountMemberPayload>(
      membersPath(trustAccountId),
      payload
    );
  },

  changeMemberRole: async (
    client: HttpClient,
    trustAccountId: string,
    userId: string,
    payload: ChangeTrustAccountMemberRolePayload
  ): Promise<ApiResponse<TrustAccountMemberRoleChangeDTO>> => {
    return client.patch<
      ApiResponse<TrustAccountMemberRoleChangeDTO>,
      ChangeTrustAccountMemberRolePayload
    >(`${membersPath(trustAccountId)}/${encodeURIComponent(userId)}`, payload);
  },

  removeMember: async (
    client: HttpClient,
    trustAccountId: string,
    userId: string,
    expectedVersion?: string
  ): Promise<ApiResponse<TrustAccountMemberRemovedDTO>> => {
    return client.delete<ApiResponse<TrustAccountMemberRemovedDTO>>(
      `${membersPath(trustAccountId)}/${encodeURIComponent(userId)}`,
      {params: {expectedVersion}}
    );
  }
};
